import type { App, Plugin } from 'vue'
import type { ParamCodeItem } from '@/typings/common'
import { cloneDeep } from 'lodash-es'
export const withInstall = <T extends { name?: string; displayName?: string }>(component: T, alias?: string) => {
  const comp = component as any
  comp.install = (app: App) => {
    app.component(comp.name || comp.displayName, component)
    if (alias) {
      app.config.globalProperties[alias] = component
    }
  }
  return component as T & Plugin
}

/**
 * 通过子节点ID查找所有关联的父节点
 * @param tree 数组对象
 * @param nodeId 末级 ID
 * @param config 字段配置
 * @returns
 */
export const getTreeIds = (tree: any, nodeId: any, config?: any) => {
  const { children = 'children', id = 'id' } = config || {}

  const toFlatArray = (tree: any, pid?: any) => {
    return tree.reduce((t: any, _: any) => {
      const child = _[children]
      return [...t, pid ? { ..._, pid } : _, ...(child && child.length ? toFlatArray(child, _[id]) : [])]
    }, [])
  }

  const getIds = (flatArray: any) => {
    let ids = [nodeId]
    let child = flatArray.find((_: any) => _[id] === nodeId)
    while (child && child.pid) {
      ids = [child.pid, ...ids]
      child = flatArray.find((_: any) => _[id] === child.pid)
    }
    return ids
  }

  return getIds(toFlatArray(tree))
}

export const findByIdDeep = (list: ParamCodeItem[] | undefined, id?: any): ParamCodeItem | undefined => {
  if (!Array.isArray(list) || list.length === 0 || !id) return undefined
  for (const item of list) {
    if (item?.id === id) return item
    const hit = findByIdDeep((item as any)?.children, id)
    if (hit) return hit
  }
  return undefined
}

/**
 * 通过子节点ID查找所有关联的label
 * @param tree 数组对象
 * @param nodeId 末级 ID
 * @param config 字段配置
 * @returns
 */
export const getTreeNameList = (tree: any, nodeId: any, config?: any) => {
  const { children = 'children', id = 'id', label = 'name' } = config || {}

  const toFlatArray = (tree: any, pid?: any) => {
    return tree.reduce((t: any, _: any) => {
      const child = _[children]
      return [...t, pid ? { ..._, pid } : _, ...(child && child.length ? toFlatArray(child, _[id]) : [])]
    }, [])
  }

  const getIds = (flatArray: any) => {
    let ids = [nodeId]
    let child = flatArray.find((_: any) => _[id] === nodeId)
    while (child && child.pid) {
      ids = [child.pid, ...ids]
      child = flatArray.find((_: any) => _[id] === child.pid)
    }
    const list = ids.map((id) => {
      const find = flatArray.find((e: any) => e.id === id)
      return find ? find[label] : nodeId // 若找不到，则直接返回传入的 id
    })
    return list
  }

  return getIds(toFlatArray(tree))
}
/**
 * 格式化金额
 * @param arg1 被计算值
 * @param n 小数长度 (默认 2)
 * @returns {string}
 *
 */
export function formatPointMoney(v: number, n = 2) {
  const arg1 = v || 0
  let t1 = 0
  let t2 = 0
  let r1 = 0
  let r2 = 0
  const arg2 = Math.pow(10, n)
  try {
    t1 = arg1.toString().split('.')[1].length
  } catch (e) {}
  try {
    t2 = arg2.toString().split('.')[1].length
  } catch (e) {}
  r1 = Number(arg1.toString().replace('.', ''))
  r2 = Number(arg2.toString().replace('.', ''))
  const result = (r1 / r2) * Math.pow(10, t2 - t1)
  if (result < 0) {
    return '-' + result.toFixed(n)
  } else {
    return result.toFixed(n)
  }
}
/**
 * 初始化对象
 * @param val 需要处理的对象
 * @param initObj 初始化对象的值
 * @returns {object}
 *
 */
export function initObjectVal(val: any, initObj: any) {
  for (const key in initObj) {
    if (val[key] && Object.prototype.toString.call(val[key]) === '[object Object]') {
      initObjectVal(val[key], initObj[key])
    } else if (val[key] == null) {
      val[key] = cloneDeep(initObj[key])
    } else if (Array.isArray(val[key]) && !val[key].length) {
      val[key] = cloneDeep(initObj[key])
    }
  }
}
/**
 * 递归将对象的 null 值修改为 undefined，去掉数组中的 null、undefined 值
 * @export
 * @param {*} obj 需要修改的对象
 * @return {*} 修改后的对象
 */
export function clearInvalid(obj: any) {
  if (typeof obj !== 'object' || obj === undefined) return obj
  if (obj === null) return undefined
  const isArray = obj instanceof Array
  if (isArray) {
    for (let i = 0; i < obj.length; i++) {
      if (obj[i] == null) obj.splice(i--, 1)
    }
  }
  const result = isArray ? [] : {}
  for (const key in obj) {
    if (Object.hasOwnProperty.call(obj, key)) {
      result[key] = clearInvalid(obj[key])
    }
  }
  return result
}

/**
 * 获取当前环境
 * @returns 返回当前环境的简称
 */
export const getEnvironment = () => {
  enum Environment {
    dev = 'http://cos.dev.yunexpress.com',
    test = 'http://cos.sit.yunexpress.com',
    uat = 'https://cos-uat.yunexpress.com',
    pro = 'https://cos.yunexpress.com'
  }
  let env: 'dev' | 'test' | 'uat' | 'pro' = 'dev'
  switch (window.WEB_URL) {
    case Environment.dev:
      env = 'dev'
      break
    case Environment.test:
      env = 'test'
      break
    case Environment.uat:
      env = 'uat'
      break
    case Environment.pro:
      env = 'pro'
      break
  }
  return env
}

/**
 * 处理客户名称和客户编码的批量查询参数
 * @param queryForm 搜索参数
 * @returns
 */
export const customerListFormat = (queryForm: any) => {
  const params = cloneDeep(queryForm)
  const clueIds = params.clueId?.split(/[,，\s\n]/).filter((e: string) => e)
  const customerSerials = params.customerSerial?.split(/[,，\s\n]/).filter((e: string) => e)
  const cosIds = params.cosId?.split(/[,，\s\n]/).filter((e: string) => e)
  const customerNameList = params.customerName?.split(/[,，\n]/).filter((e: string) => e)
  const tmsCodeList = params.tmsCode?.split(/[,，\s\n]/).filter((e: string) => e)
  const followRefIds = params.followRefId?.split(/[,，\s\n]/).filter((e: string) => e)
  // 客户id批量
  const saleChanceRefIds = params.saleChanceRefId?.split(/[,，\s\n]/).filter((e: string) => e)
  // 商机id批量
  const chanceIds = params.id?.split(/[,，\s\n]/).filter((e: string) => e)
  if (clueIds && clueIds.length > 0) {
    params.clueIdList = clueIds
    delete params.clueId
  }
  if (customerSerials && customerSerials.length > 0) {
    params.customerSerialList = customerSerials
    delete params.customerSerial
  }
  if (cosIds && cosIds.length > 0) {
    params.cosIdList = cosIds
    delete params.cosId
  }
  if (customerNameList && customerNameList.length > 1) {
    params.customerNameList = customerNameList
    delete params.customerName
  }
  if (tmsCodeList && tmsCodeList.length > 0) {
    params.tmsCodeList = tmsCodeList
    delete params.tmsCode
  }
  if (followRefIds && followRefIds.length > 0) {
    params.followRefIdList = followRefIds
    delete params.followRefId
  }
  if (saleChanceRefIds && saleChanceRefIds.length > 0) {
    params.saleChanceRefIdList = saleChanceRefIds
    delete params.saleChanceRefId
  }
  if (chanceIds && chanceIds.length > 1) {
    params.idList = chanceIds
    delete params.id
  }
  return params
}
/**
 * 通过路由生成tableId
 * @param key 后缀
 * @returns
 */
export const tableIdByPath = (key: string) => {
  let prev = window.location.pathname.replace('/dc-cos-lbu/', '')
  prev = prev.replaceAll('/', '_')
  return prev + '_' + key
}

/**
 * 下载base64图片
 * @param content 图片地址
 * @param fileName 图片名称
 */
export const downloadFileByBase64 = (content: string, fileName: string) => {
  const base64ToBlob = function (code: string) {
    const parts = code.split(';base64,')
    const contentType = parts[0].split(':')[1]
    const raw = window.atob(parts[1])
    const rawLength = raw.length
    const uInt8Array = new Uint8Array(rawLength)
    for (let i = 0; i < rawLength; ++i) {
      uInt8Array[i] = raw.charCodeAt(i)
    }
    return new Blob([uInt8Array], {
      type: contentType
    })
  }
  const aLink = document.createElement('a')
  const blob = base64ToBlob(content) // new Blob([content]);
  const evt = document.createEvent('HTMLEvents')
  evt.initEvent('click', true, true) // initEvent 不加后两个参数在FF下会报错  事件类型，是否冒泡，是否阻止浏览器的默认行为
  aLink.download = fileName
  aLink.href = URL.createObjectURL(blob)
  aLink.click()
}

/**
 * 递归过滤业务参数状态
 * @param dataObj 原始对象数据
 * @param status 业务参数状态【0-可用,1-禁用】
 * @returns 过滤后的数据对象
 */
export const paramCodesDataFilter = (dataObj: { [key: string]: ParamCodeItem[] }, status: 0 | 1) => {
  const cloneData = cloneDeep(dataObj)
  const recursion = (data: ParamCodeItem[]) => {
    return data.filter((item) => {
      if (item.children) item.children = recursion(item.children)
      return item.status === status
    })
  }
  for (const key in cloneData) {
    if (Object.prototype.hasOwnProperty.call(cloneData, key)) {
      cloneData[key] = recursion(cloneData[key] || [])
    }
  }
  return cloneData
}

/**
 * 获取url参数
 * @param name 需要获取的值
 * @returns
 */
export const getUrlParam = (name: string) => {
  const url = window.location.href
  const parts = url.split('?')
  if (parts.length < 2) return undefined
  const params = parts[1].split('&')

  const obj = {}
  params.forEach((param) => {
    const parts = param.split('=')
    obj[parts[0]] = parts[1]
  })

  return obj[name]
}
